<script lang="ts">
  // *** This file is part of DbGate Premium ***

  import { ChartDefinition, ProcessedChart } from 'dbgate-datalib';
  import ChartSelector from './ChartSelector.svelte';
  import ChartDisplay from './ChartDisplay.svelte';
  import InlineButton from '../buttons/InlineButton.svelte';
  import FontIcon from '../icons/FontIcon.svelte';
  import FormProviderCore from '../forms/FormProviderCore.svelte';
  import { writable } from 'svelte/store';
  import FormFieldTemplateTiny from '../forms/FormFieldTemplateTiny.svelte';
  import HorizontalSplitter from '../elements/HorizontalSplitter.svelte';
  import ChartDefinitionEditor from './ChartDefinitionEditor.svelte';
  import { apiCall } from '../utility/api';
  import { showSnackbarError } from '../utility/snackbar';

  export let jslid;
  export let initialCharts: ProcessedChart[];
  export let onEditDefinition;
  export let onCloseChart;

  $: selectedChart = initialCharts?.length == 1 ? initialCharts[0] : null;

  let editorVisible = false;
  let managerSize;

  const handleDefinitionChange = async changeFunc => {
    const newDefinition = changeFunc(selectedChart.definition);
    try {
      const { charts } = await apiCall('jsldata/build-chart', { jslid, definition: newDefinition });
      // console.log('New charts', newCharts);
      selectedChart = charts[0];
      onEditDefinition?.(newDefinition);
    } catch (err) {
      selectedChart = {
        ...selectedChart,
        definition: newDefinition,
      };
      onEditDefinition?.(newDefinition);
    }
  };

  const handleReset = async () => {
    const { charts } = await apiCall('jsldata/build-chart', { jslid });
    initialCharts = charts;
    editorVisible = false;
    onEditDefinition?.(null);
  };

  const loadEmptyChart = async () => {
    const { charts, columns } = await apiCall('jsldata/build-chart', { jslid });
    if (charts?.length) {
      initialCharts = charts;
      editorVisible = false;
      onEditDefinition?.(null);
    } else {
      const definition: ChartDefinition = {
        chartType: 'bar',
        xdef: {
          field: columns[0].field,
          transformFunction: 'identity',
        },

        ydefs: [],
      };
      selectedChart = {
        definition,
        availableColumns: columns,
      } as ProcessedChart;
      editorVisible = true;
    }
  };

  $: if (!initialCharts?.length && !selectedChart) {
    loadEmptyChart();
  }
</script>

{#if !selectedChart && initialCharts?.length > 1}
  <ChartSelector
    charts={initialCharts}
    onChoose={chart => {
      selectedChart = chart;
      onEditDefinition?.(chart.definition);
    }}
  />
{:else}
  <HorizontalSplitter
    isSplitter={editorVisible}
    initialSizeRight={300}
    onChangeSize={(_size, rsize) => {
      managerSize = rsize;
    }}
  >
    <svelte:fragment slot="1">
      {#if selectedChart}
        <ChartDisplay chart={selectedChart} />

        <div class="editor-button">
          {#if onCloseChart}
            <InlineButton on:click={onCloseChart} data-testid="JslChart_closeButton">
              <FontIcon icon="icon close" />
              Close
            </InlineButton>
          {/if}

          <InlineButton on:click={handleReset} data-testid="JslChart_resetButton">
            <FontIcon icon="icon reset" />
            Reset
          </InlineButton>

          {#if !editorVisible}
            <InlineButton
              on:click={() => {
                editorVisible = true;
              }}
              data-testid="JslChart_customizeButton"
            >
              <FontIcon icon="icon edit" />
              Customize
            </InlineButton>
          {/if}
        </div>
      {/if}
    </svelte:fragment>
    <svelte:fragment slot="2">
      {#if selectedChart}
        <ChartDefinitionEditor
          onClose={() => {
            editorVisible = false;
          }}
          chart={selectedChart}
          onChange={handleDefinitionChange}
          {managerSize}
        />
      {/if}
    </svelte:fragment>
  </HorizontalSplitter>
{/if}

<style>
  .editor-button {
    position: absolute;
    display: flex;
    top: 0;
    right: 0;
  }
</style>
